import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
    ArrowBackOutlined,
    AddOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
} from "@mui/material";
import { useUpdateOrgMutation } from "state/rtkQuery/org/orgMutations";
import ChipArrayInput from "components/shared/ChipArrayInput/ChipArrayInput";

import "./EcrOpsCompleteOrg.css";

// ── Section heading ───────────────────────────────────────────────────────────

const SectionHeading = ({ title, subtitle = null }) => (
    <div className="ecr-complete-org__section-head">
        <p className="ecr-complete-org__section-title">{title}</p>
        {subtitle && <p className="ecr-complete-org__section-sub">{subtitle}</p>}
    </div>
);

SectionHeading.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
};


// ── Toggle row ────────────────────────────────────────────────────────────────

const ToggleRow = ({ label, description = null, checked, onChange }) => (
    <div className="ecr-complete-org__toggle-row">
        <div className="ecr-complete-org__toggle-text">
            <span className="ecr-complete-org__toggle-label">{label}</span>
            {description && (
                <span className="ecr-complete-org__toggle-desc">{description}</span>
            )}
        </div>
        <FormControlLabel
            control={
                <Switch
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    size="small"
                    sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#86BC25" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#86BC25",
                        },
                    }}
                />
            }
            label={checked ? "Yes" : "No"}
            sx={{ margin: 0, "& .MuiFormControlLabel-label": { fontSize: "13px" } }}
        />
    </div>
);

ToggleRow.propTypes = {
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    checked: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
};


// ── Form initial state ────────────────────────────────────────────────────────

const buildInitial = (orgName) => ({
    name: orgName || "",
    membership_ad_group: "",
    admin_ad_group: "",
    org_type: "",
    description: "",
    signup_banner: "",
    service_owner: "",
    owners: [],
    owners_email: [],
    approvers: [],
    countries: [],
    visible: true,
    approval_required: false,
    user_removal_enabled: false,
    inactive_user_removal_enabled: false,
    github_org_link: "",
    org_sync_is_enabled: false,
    org_sync_is_owners_sync_enabled: false,
    org_sync_ignored_emails: [],
    org_sync_owners_user_name: [],
});

// ── Main component ────────────────────────────────────────────────────────────

const EcrOpsCompleteOrg = () => {
    const navigate = useNavigate();
    const { orgId } = useParams();
    const location = useLocation();
    const orgName = location.state?.orgName || "";

    const [form, setForm] = useState(() => buildInitial(orgName));
    const [errors, setErrors] = useState({});

    const [updateOrg, { isLoading, isError, error }] = useUpdateOrgMutation();

    const set = (field) => (val) =>
        setForm((f) => ({ ...f, [field]: val }));

    const setFromEvent = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
        setErrors((err) => ({ ...err, [field]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Organisation name is required.";
        if (!form.membership_ad_group.trim()) e.membership_ad_group = "Membership AD group is required.";
        if (!form.admin_ad_group.trim()) e.admin_ad_group = "Admin AD group is required.";
        if (!form.org_type) e.org_type = "Organisation type is required.";
        if (form.owners.length === 0) e.owners = "At least one owner is required.";
        if (form.approvers.length === 0) e.approvers = "At least one approver is required.";
        if (form.countries.length === 0) e.countries = "At least one country is required.";
        return e;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        const body = {
            name: form.name.trim(),
            membership_ad_group: form.membership_ad_group.trim(),
            admin_ad_group: form.admin_ad_group.trim(),
            org_type: form.org_type,
            description: form.description.trim() || undefined,
            signup_banner: form.signup_banner.trim() || undefined,
            service_owner: form.service_owner.trim() || undefined,
            owners: form.owners,
            owners_email: form.owners_email,
            approvers: form.approvers,
            countries: form.countries,
            visible: form.visible,
            approval_required: form.approval_required,
            user_removal_enabled: form.user_removal_enabled,
            inactive_user_removal_enabled: form.inactive_user_removal_enabled,
            additional_info: {
                github_org_link: form.github_org_link.trim() || undefined,
            },
            org_sync_config: {
                is_enabled: form.org_sync_is_enabled,
                is_owners_sync_enabled: form.org_sync_is_owners_sync_enabled,
                ignored_emails: form.org_sync_ignored_emails,
                owners_user_name: form.org_sync_owners_user_name,
            },
        };

        try {
            await updateOrg({ orgId, body }).unwrap();
            navigate("/ecr-ops/org-management", {
                state: { successMessage: `"${form.name}" has been completed and is now Active.` },
            });
        } catch {
            // isError and error are set by RTK Query
        }
    };

    return (
        <div>
            {/* Page header */}
            <div className="ecr-ops-page-header">
                <p className="ecr-ops-page-header__eyebrow">ECR Ops Portal · New Organisations</p>
                <h1 className="ecr-ops-page-header__title">Complete Org Creation</h1>
                <p className="ecr-ops-page-header__sub">
                    Fill in the required configuration for <strong>{orgName || orgId}</strong> and submit to
                    activate this organisation.
                </p>
            </div>

            <div className="ecr-ops-content">
                {/* Back link */}
                <Button
                    startIcon={<ArrowBackOutlined />}
                    size="small"
                    onClick={() => navigate("/ecr-ops/new-orgs")}
                    sx={{
                        textTransform: "none",
                        color: "#6b7280",
                        fontSize: "13px",
                        padding: "4px 0",
                        mb: 2,
                        "&:hover": { background: "transparent", color: "#86BC25" },
                    }}
                >
                    Back to New Organisations
                </Button>

                {/* Org identity banner */}
                <div className="ecr-complete-org__identity-banner">
                    <div className="ecr-complete-org__identity-item">
                        <span className="ecr-complete-org__identity-label">Org ID</span>
                        <code className="ecr-complete-org__identity-value">{orgId}</code>
                    </div>
                    <div className="ecr-complete-org__identity-item">
                        <span className="ecr-complete-org__identity-label">Status</span>
                        <span className="ecr-complete-org__identity-badge">In-Progress</span>
                    </div>
                </div>

                <form onSubmit={handleSubmit} noValidate className="ecr-complete-org__form">

                    {/* ── 1. Basic Info ─────────────────────────────────────────────── */}
                    <div className="ecr-complete-org__card">
                        <SectionHeading
                            title="Basic Information"
                            subtitle="Core identity fields for the organisation."
                        />
                        <div className="ecr-complete-org__fields">
                            <TextField
                                label="Organisation Name"
                                required
                                size="small"
                                value={form.name}
                                onChange={setFromEvent("name")}
                                error={!!errors.name}
                                helperText={errors.name}
                                fullWidth
                            />

                            <TextField
                                label="Description"
                                size="small"
                                value={form.description}
                                onChange={setFromEvent("description")}
                                multiline
                                minRows={2}
                                fullWidth
                                placeholder="Briefly describe this organisation's purpose…"
                            />

                            <TextField
                                label="Signup Banner"
                                size="small"
                                value={form.signup_banner}
                                onChange={setFromEvent("signup_banner")}
                                multiline
                                minRows={2}
                                fullWidth
                                placeholder="Message shown to users when requesting access…"
                            />

                            <FormControl fullWidth size="small" required error={!!errors.org_type}>
                                <InputLabel>Organisation Type</InputLabel>
                                <Select
                                    value={form.org_type}
                                    label="Organisation Type"
                                    onChange={setFromEvent("org_type")}
                                >
                                    <MenuItem value="US">US</MenuItem>
                                    <MenuItem value="Global">Global</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                </Select>
                                {errors.org_type && (
                                    <p className="ecr-complete-org__select-error">{errors.org_type}</p>
                                )}
                            </FormControl>
                        </div>
                    </div>

                    {/* ── 2. AD Groups ─────────────────────────────────────────────── */}
                    <div className="ecr-complete-org__card">
                        <SectionHeading
                            title="Active Directory Groups"
                            subtitle="AD groups that control membership and admin access to this org."
                        />
                        <div className="ecr-complete-org__fields">
                            <TextField
                                label="Membership AD Group"
                                required
                                size="small"
                                value={form.membership_ad_group}
                                onChange={setFromEvent("membership_ad_group")}
                                error={!!errors.membership_ad_group}
                                helperText={errors.membership_ad_group}
                                fullWidth
                                placeholder="e.g. SG-US-GITHUB EMU-Deloitte US Consulting NBI"
                            />
                            <TextField
                                label="Admin AD Group"
                                required
                                size="small"
                                value={form.admin_ad_group}
                                onChange={setFromEvent("admin_ad_group")}
                                error={!!errors.admin_ad_group}
                                helperText={errors.admin_ad_group}
                                fullWidth
                                placeholder="e.g. SG-US-GITHUB EMU-Deloitte US Consulting NBI-Admins"
                            />
                        </div>
                    </div>

                    {/* ── 3. People ────────────────────────────────────────────────── */}
                    <div className="ecr-complete-org__card">
                        <SectionHeading
                            title="Owners & Approvers"
                            subtitle="Email addresses for org owners, owner emails for notifications, and approvers."
                        />
                        <div className="ecr-complete-org__fields">
                            <ChipArrayInput
                                label="Owners"
                                required
                                value={form.owners}
                                onChange={(v) => { set("owners")(v); setErrors((e) => ({ ...e, owners: undefined })); }}
                                error={!!errors.owners}
                                helperText={errors.owners}
                                placeholder="name@deloitte.com and press Enter…"
                            />
                            <ChipArrayInput
                                label="Owner Emails (for notifications)"
                                value={form.owners_email}
                                onChange={set("owners_email")}
                                placeholder="name@deloitte.com and press Enter…"
                            />
                            <ChipArrayInput
                                label="Approvers"
                                required
                                value={form.approvers}
                                onChange={(v) => { set("approvers")(v); setErrors((e) => ({ ...e, approvers: undefined })); }}
                                error={!!errors.approvers}
                                helperText={errors.approvers}
                                placeholder="name@deloitte.com and press Enter…"
                            />
                            <TextField
                                label="Service Owner"
                                size="small"
                                value={form.service_owner}
                                onChange={setFromEvent("service_owner")}
                                fullWidth
                                placeholder="service-owner@deloitte.com"
                            />
                        </div>
                    </div>

                    {/* ── 4. Location ──────────────────────────────────────────────── */}
                    <div className="ecr-complete-org__card">
                        <SectionHeading
                            title="Location"
                            subtitle="Countries this organisation operates in."
                        />
                        <ChipArrayInput
                            label="Countries"
                            required
                            value={form.countries}
                            onChange={(v) => { set("countries")(v); setErrors((e) => ({ ...e, countries: undefined })); }}
                            error={!!errors.countries}
                            helperText={errors.countries}
                            placeholder="e.g. US, UK, IN and press Enter…"
                        />
                    </div>

                    {/* ── 5. Additional Info ───────────────────────────────────────── */}
                    <div className="ecr-complete-org__card">
                        <SectionHeading
                            title="Additional Info"
                            subtitle="Optional links and metadata."
                        />
                        <div className="ecr-complete-org__fields">
                            <TextField
                                label="GitHub Org Link"
                                size="small"
                                value={form.github_org_link}
                                onChange={setFromEvent("github_org_link")}
                                fullWidth
                                placeholder="https://github.com/Deloitte-…"
                            />
                        </div>
                    </div>

                    {/* ── 6. Settings ──────────────────────────────────────────────── */}
                    <div className="ecr-complete-org__card">
                        <SectionHeading
                            title="Settings"
                            subtitle="Visibility, access controls, and user management behaviour."
                        />
                        <div className="ecr-complete-org__toggles">
                            <ToggleRow
                                label="Visible"
                                description="Show this organisation in the portal for users to discover and join."
                                checked={form.visible}
                                onChange={set("visible")}
                            />
                            <Divider />
                            <ToggleRow
                                label="Approval Required"
                                description="Membership requests must be approved before access is granted."
                                checked={form.approval_required}
                                onChange={set("approval_required")}
                            />
                            <Divider />
                            <ToggleRow
                                label="User Removal Enabled"
                                description="Allow automated removal of users who no longer meet membership criteria."
                                checked={form.user_removal_enabled}
                                onChange={set("user_removal_enabled")}
                            />
                            <Divider />
                            <ToggleRow
                                label="Inactive User Removal Enabled"
                                description="Automatically remove users who have been inactive for an extended period."
                                checked={form.inactive_user_removal_enabled}
                                onChange={set("inactive_user_removal_enabled")}
                            />
                        </div>
                    </div>

                    {/* ── 7. Org Sync Config ───────────────────────────────────────── */}
                    <div className="ecr-complete-org__card">
                        <SectionHeading
                            title="Org Sync Configuration"
                            subtitle="Settings for automated org membership synchronisation."
                        />
                        <div className="ecr-complete-org__fields">
                            <div className="ecr-complete-org__toggles">
                                <ToggleRow
                                    label="Org Sync Enabled"
                                    description="Enable automated sync of org membership from AD."
                                    checked={form.org_sync_is_enabled}
                                    onChange={set("org_sync_is_enabled")}
                                />
                                <Divider />
                                <ToggleRow
                                    label="Org Owners Email Sync Enabled"
                                    description="Sync org owner emails automatically."
                                    checked={form.org_sync_is_owners_sync_enabled}
                                    onChange={set("org_sync_is_owners_sync_enabled")}
                                />
                            </div>
                            <ChipArrayInput
                                label="Ignored Emails"
                                value={form.org_sync_ignored_emails}
                                onChange={set("org_sync_ignored_emails")}
                                placeholder="email@deloitte.com and press Enter…"
                            />
                            <ChipArrayInput
                                label="Owners Username (sync)"
                                value={form.org_sync_owners_user_name}
                                onChange={set("org_sync_owners_user_name")}
                                placeholder="github-username and press Enter…"
                            />
                        </div>
                    </div>

                    {/* ── Error banner ─────────────────────────────────────────────── */}
                    {isError && (
                        <Alert severity="error" sx={{ fontSize: "13px" }}>
                            {error?.data?.message || "Failed to complete org creation. Please check the details and try again."}
                        </Alert>
                    )}

                    {/* ── Actions ──────────────────────────────────────────────────── */}
                    <div className="ecr-complete-org__actions">
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate("/ecr-ops/new-orgs")}
                            disabled={isLoading}
                            sx={{
                                textTransform: "none",
                                borderColor: "#d1d5db",
                                color: "#374151",
                                fontSize: "13px",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            size="small"
                            disabled={isLoading}
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={14} sx={{ color: "#fff" }} />
                                ) : (
                                    <AddOutlined />
                                )
                            }
                            sx={{
                                textTransform: "none",
                                background: "#86BC25",
                                fontSize: "13px",
                                fontWeight: 600,
                                padding: "7px 20px",
                                "&:hover": { background: "#6fa019" },
                            }}
                        >
                            {isLoading ? "Saving…" : "Complete Org Creation"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EcrOpsCompleteOrg;
